/**
 * Filter matches based on time range selection
 * @param {Array} matches - Array of match objects
 * @param {string} timeRange - Time range filter value
 * @returns {Array} Filtered matches
 */
export const filterMatchesByTimeRange = (matches, timeRange) => {
  if (!matches || matches.length === 0 || timeRange === 'all') {
    return matches || [];
  }

  // Handle custom date range
  if (timeRange.startsWith('custom:')) {
    const [, startDate, endDate] = timeRange.split(':');
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999); // Include full end date

    return matches.filter(match => {
      const matchDate = new Date(match.timestamp || match.date);
      return matchDate >= start && matchDate <= end;
    });
  }

  const now = new Date();
  const cutoffDate = new Date();

  switch (timeRange) {
    case '7d':
      cutoffDate.setDate(now.getDate() - 7);
      break;
    case '30d':
      cutoffDate.setDate(now.getDate() - 30);
      break;
    case '90d':
      cutoffDate.setDate(now.getDate() - 90);
      break;
    case '6m':
      cutoffDate.setMonth(now.getMonth() - 6);
      break;
    case 'year':
      cutoffDate.setFullYear(now.getFullYear());
      cutoffDate.setMonth(0);
      cutoffDate.setDate(1);
      break;
    default:
      return matches;
  }

  return matches.filter(match => {
    const matchDate = new Date(match.timestamp || match.date);
    return matchDate >= cutoffDate;
  });
};

/**
 * Filter player statistics based on time range
 * @param {Object} playerStats - Player statistics object
 * @param {Array} filteredMatches - Time-filtered matches
 * @param {Array} allPlayers - All players array
 * @returns {Object} Filtered player statistics
 */
export const filterPlayerStatsByTimeRange = (
  playerStats,
  filteredMatches,
  allPlayers
) => {
  if (!playerStats || !filteredMatches) return playerStats;

  const filteredStats = {};

  // Only recalculate TrueSkill ratings if we have fewer matches
  // (meaning we're actually filtering)
  const totalMatches =
    Object.values(playerStats).reduce(
      (sum, p) => sum + (p.wins || 0) + (p.losses || 0),
      0
    ) / 4; // Divide by 4 since each match counts for 4 players
  const shouldRecalculateTrueSkill = filteredMatches.length < totalMatches;

  const filteredTrueSkillStats = shouldRecalculateTrueSkill
    ? calculateFilteredTrueSkillRatings(allPlayers, filteredMatches)
    : {};

  // Recalculate stats for each player based on filtered matches
  allPlayers.forEach(player => {
    const playerMatches = filteredMatches.filter(
      m =>
        m &&
        m.winners &&
        m.losers &&
        (m.winners.includes(player.id) || m.losers.includes(player.id))
    );

    const wins = playerMatches.filter(m =>
      m.winners.includes(player.id)
    ).length;
    const losses = playerMatches.filter(m =>
      m.losers.includes(player.id)
    ).length;

    // Calculate bread statistics for filtered time period
    let earnedFullSlices = 0;
    let earnedHalfSlices = 0;
    let lifetimeFullSlices = 0;
    let lifetimeHalfSlices = 0;

    filteredMatches.forEach(match => {
      if (match && match.losers && match.losers.includes(player.id)) {
        const [s1, s2] = match.score.split('-').map(Number);
        if (s1 === 10 && s2 === 0) {
          earnedFullSlices++;
          lifetimeFullSlices++;
        }
        if (s1 === 10 && s2 === 1) {
          earnedHalfSlices++;
          lifetimeHalfSlices++;
        }
      }
    });

    // Filter history to only include matches in time range
    const originalStats = playerStats[player.id] || {};
    const filteredHistory = (originalStats.history || []).filter(h => {
      return filteredMatches.some(m => m.id === h.matchId);
    });

    // Get filtered TrueSkill ratings - only use recalculated ratings
    // if we actually filtered
    const filteredRatings =
      shouldRecalculateTrueSkill && filteredTrueSkillStats[player.id]
        ? filteredTrueSkillStats[player.id]
        : { mu: player.mu, sigma: player.sigma };

    filteredStats[player.id] = {
      ...originalStats,
      ...player,
      // Use filtered TrueSkill ratings for chart display only
      // when actually filtering
      mu: filteredRatings.mu,
      sigma: filteredRatings.sigma,
      filteredWins: wins,
      filteredLosses: losses,
      filteredGames: wins + losses,
      history: filteredHistory,
      // Override bread stats with filtered values
      earnedFullSlices,
      earnedHalfSlices,
      lifetimeFullSlices,
      lifetimeHalfSlices,
      // Keep original paid values
      paidFullSlices:
        originalStats.paidFullSlices || player.paidFullSlices || 0,
      paidHalfSlices:
        originalStats.paidHalfSlices || player.paidHalfSlices || 0,
    };
  });

  return filteredStats;
};

/**
 * Calculate TrueSkill ratings using only matches within time range
 * @param {Array} players - All players array
 * @param {Array} filteredMatches - Time-filtered matches
 * @returns {Object} Object with player IDs as keys and filtered ratings
 * as values
 */
const calculateFilteredTrueSkillRatings = (players, filteredMatches) => {
  // Import TrueSkill calculator dynamically to avoid circular imports
  const {
    trueSkillCalculator,
  } = require('../calculations/trueSkillCalculator.js');

  const filteredRatings = {};

  // Initialize all players with default TrueSkill ratings
  players.forEach(p => {
    filteredRatings[p.id] = {
      id: p.id,
      mu: 25.0, // Default starting rating
      sigma: 8.333, // Default starting uncertainty
      wins: 0,
      losses: 0,
    };
  });

  // Sort matches by timestamp to process in chronological order
  const sortedMatches = [...filteredMatches]
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // Process each match to recalculate TrueSkill ratings
  sortedMatches.forEach(m => {
    if (!m || !m.winners || !m.losers) return;

    const matchPlayerIds = [...m.winners, ...m.losers];
    const matchPlayers = matchPlayerIds
      .map(pid => filteredRatings[pid])
      .filter(p => p); // Only include players we have ratings for

    if (matchPlayers.length === 4) {
      // Use actual TrueSkill calculation to get updated ratings
      const updatedPlayers = trueSkillCalculator.processMatch(m, matchPlayers);

      // Update the filtered ratings
      updatedPlayers.forEach(updatedPlayer => {
        if (filteredRatings[updatedPlayer.id]) {
          filteredRatings[updatedPlayer.id].mu = updatedPlayer.mu;
          filteredRatings[updatedPlayer.id].sigma = updatedPlayer.sigma;
          filteredRatings[updatedPlayer.id].wins = updatedPlayer.wins;
          filteredRatings[updatedPlayer.id].losses = updatedPlayer.losses;
        }
      });
    }
  });

  return filteredRatings;
};
